import { getAuth } from "firebase/auth";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { message } from "antd";
import * as APITypes from "~/API";
import { INTENTS, ROUTES } from "~/utils";
import { apiClient, type AddServiceRequestLeaseOptionsLoaderResult } from "~/services/apiClient";
import { sendEmail } from "~/services/email/sendEmail";
import type { EmailObject } from "~/types/email";

function debugAddServiceRequestAction(step: string, detail?: unknown) {
  if (process.env.NODE_ENV !== "test") return;
  if (detail !== undefined) {
    console.log(`[AddServiceRequestPageAction] ${step}`, detail);
  } else {
    console.log(`[AddServiceRequestPageAction] ${step}`);
  }
}

export const AddServiceRequestPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect("/signup");

  /** Active leases for this tenant (unit – building – property); first option pre-selects in the form. */
  const leaseRes = await apiClient.loader<AddServiceRequestLeaseOptionsLoaderResult>(
    "service-request-lease-options",
  );

  const leaseData: AddServiceRequestLeaseOptionsLoaderResult =
    leaseRes.success && leaseRes.data
      ? leaseRes.data
      : { leaseOptions: [], defaultLeaseId: null };

  return {
    user: {
      userID: user?.uid,
      tenantID: user?.tenantId,
      tenantName: user?.displayName,
    },
    leaseOptions: leaseData.leaseOptions,
    defaultLeaseId: leaseData.defaultLeaseId,
  };
};

export type AddServiceRequestPageLoaderType = {
  user: {
    userID: string;
    tenantID: string;
    tenantName: string;
  };
  leaseOptions: AddServiceRequestLeaseOptionsLoaderResult["leaseOptions"];
  defaultLeaseId: string | null;
};

export type AddServiceRequestPageActionError = {
  ok: false;
  error: string;
};

export const AddServiceRequestPageAction = async ({
  request,
}: ActionFunctionArgs) => {
  debugAddServiceRequestAction("1: reading formData");
  const formData = await request.formData();
  const auth = getAuth();
  const user = auth.currentUser;
  debugAddServiceRequestAction("2: auth", { hasUser: Boolean(user), uid: user?.uid });
  if (!user) {
    debugAddServiceRequestAction("FAIL: not authenticated");
    message.error("Not authenticated");
    return { ok: false as const, error: "Not authenticated" };
  }

  let payload: {
    leaseId: string;
    type: APITypes.ServiceRequestType;
    description: string;
    title: string;
    emergency: boolean;
    isUpgrade: boolean;
  };
  debugAddServiceRequestAction("3: parsing body JSON");
  try {
    payload = JSON.parse((formData.get("body") as string) ?? "{}") as typeof payload;
  } catch {
    debugAddServiceRequestAction("FAIL: invalid JSON body");
    message.error("Invalid form data");
    return { ok: false as const, error: "Invalid form data" };
  }

  debugAddServiceRequestAction("4: parsed payload", {
    type: payload.type,
    title: payload.title,
  });

  debugAddServiceRequestAction("5: calling sendIntent", INTENTS._add_service_request);
  const result = await apiClient.sendIntent<{
    serviceRequest: unknown;
    emailObject: EmailObject;
  }>(INTENTS._add_service_request, {
    authUID: user.uid,
    creatorName: user.displayName ?? "",
    ...payload,
  });
  debugAddServiceRequestAction("6: sendIntent result", {
    success: result.success,
    hasData: Boolean(result.data),
    error: result.error,
    lambdaDebugLogCount: result.debugLogs?.length ?? 0,
  });
  if (result.debugLogs?.length) {
    debugAddServiceRequestAction(
      "6b: Lambda debugLogs (from API body)",
      result.debugLogs,
    );
  }

  if (!result.success || !result.data) {
    debugAddServiceRequestAction("FAIL: sendIntent unsuccessful");
    const errMsg = result.error ?? "Failed to create service request";
    message.error(errMsg);
    return { ok: false as const, error: errMsg };
  }

  debugAddServiceRequestAction("7: calling sendEmail");
  try {
    const emailResult = await sendEmail(result.data.emailObject as EmailObject);
    debugAddServiceRequestAction("8: sendEmail result", {
      success: emailResult.success,
    });
    if (!emailResult.success) {
      message.error("Service request created but email failed to send");
    } else {
      message.success("Service request added!");
    }
  } catch (emailErr) {
    debugAddServiceRequestAction("FAIL: sendEmail threw", emailErr);
    const msg =
      emailErr instanceof Error ? emailErr.message : String(emailErr);
    message.error(msg);
    return { ok: false as const, error: msg };
  }

  debugAddServiceRequestAction("9: redirecting to service request table");
  return redirect(ROUTES.serviceRequestTablePage);
};
